/**
 * AI CONTEXT
 * Purpose: the Quality Check tile surface — the review queue laid out as a
 *   Competition section above day groups of class sections, each a grid of
 *   square letterboxed tiles at a slider-set pixel width, with the same size
 *   slider and fullscreen gallery as the student profile's Artwork tab
 *   (shared ArtworkTiles primitives). Each tile is ArtworkEditableThumb:
 *   hover crop, rotate, and share, and a click opens the full-screen view
 *   with those same controls. Crop from the tile opens that view and its
 *   crop tools. A tile's outline says whether it is shared (green), Don't
 *   share (red), or still open (transparent) — an outline, so deciding never
 *   shifts the grid.
 * Data: no reads — the layout arrives already filtered, windowed, grouped,
 *   and version-deduped (`buildQualityCheckLayout`). The thumb does not
 *   subscribe and does not resolve a Storage download URL; it shows
 *   `cropUrl`, `thumbnailUrl`, or `url` already on the item.
 *   Writes stay in the view: rotation (the shared queue), family share
 *   (the share / don't-share icons, on hover in the bottom third), Share
 *   remaining / Don't share remaining per section (the Don't share reason
 *   box lives here),
 *   hide, and perspective crop (`onCropSave` / `onCropRevert`).
 * Referenced by: QualityCheckView.
 */
import {
  CheckOutlined,
  DeleteOutlined,
  FullscreenOutlined,
  StopOutlined,
} from "@ant-design/icons"
import {
  Button,
  Checkbox,
  Empty,
  Modal,
  Popconfirm,
  Space,
  Tooltip,
  Typography,
} from "antd"
import { type CSSProperties, useCallback, useState } from "react"
import styled from "styled-components"

import Flex from "#blocks/Flex"
import { type ArtworkCropResult } from "#components/ArtworkTiles/ArtworkCropModal"
import ArtworkEditableThumb, {
  ARTWORK_EDIT_SOURCE,
} from "#components/ArtworkTiles/ArtworkEditableThumb"
import {
  FULLSCREEN_BG,
  getTierForTileWidth,
  MAX_TILE_WIDTH,
  secondaryTextStyle,
  SScrollArea,
  SStickyControls,
  STileGrid,
  STileSquare,
  THUMBNAIL_TILE_WIDTH,
  TileWidthSlider,
  useBrowserFullscreen,
} from "#components/ArtworkTiles/artworkTilePrimitives"
import { useArtworkRotationQueue } from "#components/ArtworkTiles/useArtworkRotationQueue"
import { type ArtworkWallItem } from "#components/ArtworkWall/artworkWallTypes"
import { isClassImageShared } from "#components/forms/AddStudent/studentArtworkTilesHelpers"
import DontShareReasonModal from "#components/views/StandoutReview/DontShareReasonModal"
import {
  ShareChoice,
  type ShareChoiceState,
} from "#components/views/StandoutReview/ShareChoice"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import { parseStoragePathFromUrl } from "#utils/artworkRenditionHelpers"

import {
  getDontShareAllTargets,
  getQualityCheckDecision,
  getQualityCheckSectionChoice,
  getShareAllTargets,
  QUALITY_CHECK_DECISION,
  type QualityCheckClassSection,
  type QualityCheckDecision,
  type QualityCheckLayout,
} from "./qualityCheckHelpers"

const { Text, Title } = Typography

/** COLORS.GRAY2 (#E3E3E8) darkened 20%, so the letterbox reads on the warm card. */
const PHOTO_CARD_BG = "#B6B6BA"

/** 12px icons at 2.5×, with room inside the button for the glyph. */
const TILE_CHOICE_BUTTON = 30
const TILE_CHOICE_ICON = 18

/** The decision outline. Outline, not border: it never changes layout. */
const DECISION_OUTLINE_PX = 3

/** Version and judging chips. The thumb places these, with Shared, at the bottom. */
const SBadge = styled.span`
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  line-height: 16px;
  color: #fff;
  background: rgba(0, 0, 0, 0.65);
`

const QualityCheckTile = ({
  item,
  dark,
  decision,
  versionCount,
  rotation,
  animateRotation,
  isHiding,
  isSharing,
  tileWidth,
  onRotate,
  onShare,
  onDontShare,
  onAllow,
  onHide,
  onCropSave,
  onCropRevert,
}: {
  item: ArtworkWallItem
  dark: boolean
  decision: QualityCheckDecision
  /** > 1 renders the "Latest · N versions" chip. */
  versionCount: number
  /** Display rotation: the saved angle plus any not-yet-saved clicks. */
  rotation: number
  /** Animate only while clicks are pending (see the tab's tile for why). */
  animateRotation: boolean
  isHiding: boolean
  isSharing: boolean
  /** Pixel width of this grid. A large tile uses the crop or the original. */
  tileWidth: number
  onRotate: ((item: ArtworkWallItem) => void) | undefined
  onShare: ((item: ArtworkWallItem) => void) | undefined
  onDontShare: (() => void) | undefined
  onAllow: (() => void) | undefined
  onHide: ((item: ArtworkWallItem) => void) | undefined
  onCropSave:
    | ((item: ArtworkWallItem, result: ArtworkCropResult) => Promise<void>)
    | undefined
  onCropRevert: ((item: ArtworkWallItem) => Promise<void>) | undefined
}) => {
  const theme = useThemeContext()
  const caption = item.caption ?? "Student artwork"
  const tier = getTierForTileWidth(tileWidth)
  const canCrop =
    !!onCropSave &&
    !!onCropRevert &&
    !!parseStoragePathFromUrl(item.artwork.url)

  const handleRotate = useCallback(() => onRotate?.(item), [item, onRotate])
  const handleShare = useCallback(() => onShare?.(item), [item, onShare])
  const handleHide = useCallback(() => onHide?.(item), [item, onHide])
  const handleCropSave = useCallback(
    (result: ArtworkCropResult) => {
      if (!onCropSave) return Promise.resolve()
      return onCropSave(item, result)
    },
    [item, onCropSave]
  )
  const handleCropRevert = useCallback(() => {
    if (!onCropRevert) return Promise.resolve()
    return onCropRevert(item)
  }, [item, onCropRevert])

  const choiceState: ShareChoiceState =
    decision === QUALITY_CHECK_DECISION.SHARED
      ? "shared"
      : decision === QUALITY_CHECK_DECISION.BLOCKED
        ? "blocked"
        : "open"

  const outlineColor =
    decision === QUALITY_CHECK_DECISION.SHARED
      ? theme.eric.green
      : decision === QUALITY_CHECK_DECISION.BLOCKED
        ? theme.eric.red
        : "transparent"

  const hideButton = (suffix: "tile" | "lightbox") =>
    onHide ? (
      <Popconfirm
        title="Hide this piece? It will be hidden everywhere but not deleted."
        onConfirm={handleHide}
        okText="Hide"
      >
        <Tooltip title="Hide">
          <Button
            size={suffix === "tile" ? "small" : "middle"}
            icon={<DeleteOutlined />}
            loading={isHiding}
            aria-label={`Hide ${caption}`}
            data-testid={`artwork-${suffix}-hide`}
          />
        </Tooltip>
      </Popconfirm>
    ) : null

  return (
    <STileSquare
      $dark={dark}
      data-testid="artwork-tile"
      data-artwork-id={item.id}
      data-decided={decision}
      style={{
        outline: `${DECISION_OUTLINE_PX}px solid ${outlineColor}`,
        ...(dark ? {} : { background: PHOTO_CARD_BG }),
      }}
    >
      <ArtworkEditableThumb
        studentId={item.studentId}
        artworkId={item.artwork.id}
        artwork={item.artwork}
        source={ARTWORK_EDIT_SOURCE.QUALITY_CHECK}
        tier={tier}
        fill
        objectFit="contain"
        borderRadius={0}
        alt={caption}
        caption={caption}
        subscribe={false}
        exposeArtworkId={false}
        rotateEnabled={!!onRotate}
        shownRotation={rotation}
        animateShownRotation={animateRotation}
        onRotate={onRotate ? handleRotate : undefined}
        cropEnabled={canCrop}
        onCropSave={canCrop ? handleCropSave : undefined}
        onCropRevert={canCrop ? handleCropRevert : undefined}
        showDecisionTags={false}
        extraTileActions={hideButton("tile")}
        extraLightboxActions={
          <>
            {onShare ? (
              <ShareChoice
                state={choiceState}
                buttonSize={28}
                loading={isSharing}
                shareDisabled={!item.artwork.classSessionId}
                shareTestId="qc-lightbox-share"
                blockTestId="qc-lightbox-dont-share"
                artworkId={item.artwork.id}
                onShare={handleShare}
                onUnshare={handleShare}
                onDontShare={onDontShare}
                onAllow={onAllow}
              />
            ) : null}
            {hideButton("lightbox")}
          </>
        }
        imageTags={
          versionCount > 1 || item.badge ? (
            <>
              {versionCount > 1 ? (
                <SBadge data-testid="version-badge">
                  Latest · {versionCount} versions
                </SBadge>
              ) : null}
              {item.badge ? (
                <SBadge data-testid="item-badge">{item.badge}</SBadge>
              ) : null}
            </>
          ) : undefined
        }
        bottomActions={
          onShare ? (
            <ShareChoice
              state={choiceState}
              buttonSize={TILE_CHOICE_BUTTON}
              iconSize={TILE_CHOICE_ICON}
              loading={isSharing}
              shareDisabled={!item.artwork.classSessionId}
              shareTestId="qc-share"
              blockTestId="qc-dont-share"
              artworkId={item.artwork.id}
              onShare={handleShare}
              onUnshare={handleShare}
              onDontShare={onDontShare}
              onAllow={onAllow}
            />
          ) : undefined
        }
      />
    </STileSquare>
  )
}

interface DontShareTarget {
  sectionKey: string
  items: ArtworkWallItem[]
}

const QualityCheckTiles = ({
  layout,
  emptyText,
  onRotate,
  onShare,
  onAllow,
  onHide,
  onCropSave,
  onCropRevert,
  onShareAll,
  onDontShareAll,
}: {
  /** Already filtered, windowed, grouped, and deduped; order is preserved. */
  layout: QualityCheckLayout
  emptyText: string
  /**
   * Saves batched absolute rotation targets (degrees by artwork id — the
   * items may span many students; the view groups the writes). Omitting it
   * hides the rotate buttons.
   */
  onRotate?: (rotationTargets: Record<string, number>) => Promise<void>
  /**
   * Shares or unshares one class scan (`isShared`). The view owns the write
   * and the optimistic flag — this snapshot has no listener echo. A missing
   * session is filled in by the view before the tile sees the piece.
   * Omitting it hides the share icons. The click is the only send.
   */
  onShare?: (item: ArtworkWallItem, isShared: boolean) => Promise<void>
  /** Clears Don't share on one piece so the two icons show again. */
  onAllow?: (item: ArtworkWallItem) => Promise<void>
  /**
   * Hides one piece (nondestructive delete — bad scans caught during
   * review). The view owns the write and drops the item from its snapshot,
   * which is also what closes the lightbox if it was open on the piece.
   * Omitting it removes the hide buttons, same as `onRotate`.
   */
  onHide?: (item: ArtworkWallItem) => Promise<void>
  /**
   * Saves a perspective crop: the editor hands over the encoded JPEGs (null
   * when the user only turned the piece) plus the angle to store, and the
   * view uploads beside the original and records the fields. Omitting it
   * (or `onCropRevert`) removes the crop buttons.
   */
  onCropSave?: (
    item: ArtworkWallItem,
    result: ArtworkCropResult
  ) => Promise<void>
  /** Clears a saved crop so every surface shows the original again. */
  onCropRevert?: (item: ArtworkWallItem) => Promise<void>
  /**
   * Shares a section's open pieces in one go. The view owns the write and
   * its error message. Omitting it (with `onDontShareAll`) removes the
   * section buttons.
   */
  /**
   * Shares the section's still-undecided pieces. Pieces already shared or
   * Don't share are not in the list. Omitting it (with `onDontShareAll`)
   * removes the remaining-items buttons.
   */
  onShareAll?: (items: ArtworkWallItem[]) => Promise<void>
  /** Marks the section's still-undecided pieces Don't share, one reason for the batch. */
  onDontShareAll?: (items: ArtworkWallItem[], reason: string) => Promise<void>
}) => {
  const theme = useThemeContext()
  const [tileWidth, setTileWidth] = useState(THUMBNAIL_TILE_WIDTH)
  const [fullscreenTileWidth, setFullscreenTileWidth] = useState(MAX_TILE_WIDTH)
  const [hidingId, setHidingId] = useState<string | null>(null)
  const [sharingId, setSharingId] = useState<string | null>(null)
  const [bulkPendingKey, setBulkPendingKey] = useState<string | null>(null)
  const [dontShareTarget, setDontShareTarget] =
    useState<DontShareTarget | null>(null)
  // Decided class sections start collapsed; a header click opens one.
  const [expandedKeys, setExpandedKeys] = useState<ReadonlySet<string>>(
    () => new Set()
  )
  // Hide finished items drops decided pieces from the grid but the header
  // still counts them. Show hidden puts those pieces back for one section.
  const [shownHiddenKeys, setShownHiddenKeys] = useState<ReadonlySet<string>>(
    () => new Set()
  )

  // Optimistic rotation, shared queue. NOT settleAfterSave: this surface is
  // a one-shot snapshot with no listener echo, so a pending angle stays as
  // the display override (it equals the saved angle mod 360, so a later
  // Refresh can never contradict it).
  const {
    pendingRotations,
    rotate,
    yieldToEditor,
    clearPending,
    restorePending,
  } = useArtworkRotationQueue({
    onSave: onRotate,
    settleAfterSave: false,
  })

  const handleRotate = useCallback(
    (item: ArtworkWallItem) =>
      rotate(item.artwork.id, item.artwork.rotation ?? 0),
    [rotate]
  )

  // Same pending-id pattern as the Artwork tab: the loading button swallows
  // a second click, so hide cannot double-write; the finally re-enables
  // the button if the save failed (the item only leaves the surface when the
  // view's write succeeded).
  const handleHide = useCallback(
    (item: ArtworkWallItem) => {
      if (!onHide) return
      setHidingId(item.artwork.id)
      void onHide(item).finally(() => setHidingId(null))
    },
    [onHide]
  )

  const handleShare = useCallback(
    (item: ArtworkWallItem) => {
      if (!onShare || !item.artwork.classSessionId) return
      setSharingId(item.artwork.id)
      void onShare(item, !isClassImageShared(item.artwork)).finally(() =>
        setSharingId(null)
      )
    },
    [onShare]
  )

  const handleShareAll = useCallback(
    (sectionKey: string, items: ArtworkWallItem[]) => {
      if (!onShareAll || items.length === 0) return
      setBulkPendingKey(sectionKey)
      void onShareAll(items).finally(() => setBulkPendingKey(null))
    },
    [onShareAll]
  )

  const handleDontShareConfirm = useCallback(
    (reason: string) => {
      if (!onDontShareAll || !dontShareTarget) return
      setBulkPendingKey(dontShareTarget.sectionKey)
      void onDontShareAll(dontShareTarget.items, reason).finally(() => {
        setBulkPendingKey(null)
        setDontShareTarget(null)
      })
    },
    [dontShareTarget, onDontShareAll]
  )

  const toggleShownHidden = useCallback(
    (sectionKey: string, shown: boolean) => {
      setShownHiddenKeys((previous) => {
        const next = new Set(previous)
        if (shown) next.add(sectionKey)
        else next.delete(sectionKey)
        return next
      })
    },
    []
  )

  const toggleExpanded = useCallback((sectionKey: string) => {
    setExpandedKeys((previous) => {
      const next = new Set(previous)
      if (next.has(sectionKey)) next.delete(sectionKey)
      else next.add(sectionKey)
      return next
    })
  }, [])

  const canCrop = !!onCropSave && !!onCropRevert

  const handleCropSave = useCallback(
    async (item: ArtworkWallItem, result: ArtworkCropResult) => {
      if (!onCropSave) return
      const artworkId = item.artwork.id
      // A tile rotate stays pending for the whole visit. Yield it before the
      // editor write, then drop it so the saved angle on the crop override
      // is what the tile shows.
      const shown = pendingRotations[artworkId]
      await yieldToEditor(artworkId)
      try {
        await onCropSave(item, result)
      } catch (error) {
        if (shown !== undefined) {
          restorePending(artworkId, shown)
        }
        throw error
      }
      clearPending(artworkId)
    },
    [clearPending, onCropSave, pendingRotations, restorePending, yieldToEditor]
  )

  const handleCropRevert = useCallback(
    async (item: ArtworkWallItem) => {
      if (!onCropRevert) return
      await onCropRevert(item)
    },
    [onCropRevert]
  )

  // Fullscreen re-opens at max size every time: it exists to look closely.
  const {
    isFullscreen,
    openFullscreen: openBrowserFullscreen,
    closeFullscreen,
  } = useBrowserFullscreen()

  const openFullscreen = useCallback(() => {
    setFullscreenTileWidth(MAX_TILE_WIDTH)
    openBrowserFullscreen()
  }, [openBrowserFullscreen])

  const tileCount = layout.renderedItems.length
  const isEmpty = layout.competition === null && layout.days.length === 0

  const renderGrid = (
    sectionKey: string,
    items: readonly ArtworkWallItem[],
    dark: boolean,
    width: number
  ) => (
    <STileGrid
      $tileWidth={width}
      $rowGap={0}
      $columnGap={0}
      data-testid="qc-tiles-grid"
    >
      {items.map((item) => (
        <QualityCheckTile
          key={`${sectionKey}:${item.artwork.id}`}
          item={item}
          dark={dark}
          decision={getQualityCheckDecision(item.artwork)}
          versionCount={layout.versionCounts[item.id] ?? 1}
          rotation={
            pendingRotations[item.artwork.id] ?? item.artwork.rotation ?? 0
          }
          animateRotation={item.artwork.id in pendingRotations}
          isHiding={hidingId === item.artwork.id}
          isSharing={sharingId === item.artwork.id}
          tileWidth={width}
          onRotate={onRotate ? handleRotate : undefined}
          onShare={onShare ? handleShare : undefined}
          onDontShare={
            onDontShareAll
              ? () => setDontShareTarget({ sectionKey, items: [item] })
              : undefined
          }
          onAllow={
            onAllow
              ? () => {
                  setSharingId(item.artwork.id)
                  void onAllow(item).finally(() => setSharingId(null))
                }
              : undefined
          }
          onHide={onHide ? handleHide : undefined}
          onCropSave={
            canCrop && parseStoragePathFromUrl(item.artwork.url)
              ? handleCropSave
              : undefined
          }
          onCropRevert={
            canCrop && parseStoragePathFromUrl(item.artwork.url)
              ? handleCropRevert
              : undefined
          }
        />
      ))}
    </STileGrid>
  )

  const renderBulkActions = (
    sectionKey: string,
    items: readonly ArtworkWallItem[]
  ) => {
    if (!onShareAll || !onDontShareAll) return null
    const shareTargets = getShareAllTargets(items)
    const dontShareTargets = getDontShareAllTargets(items)
    const remaining = dontShareTargets.length
    if (remaining === 0) return null
    const pending = bulkPendingKey === sectionKey
    const busy = bulkPendingKey !== null
    return (
      <Space size={8} onClick={(event) => event.stopPropagation()}>
        <Text type="secondary">{`Share remaining(${remaining}):`}</Text>
        <Tooltip title="Share remaining">
          <Button
            size="small"
            data-testid="qc-share-all"
            data-state="open"
            aria-label="Share remaining"
            disabled={busy || shareTargets.length === 0}
            loading={pending && dontShareTarget === null}
            icon={<CheckOutlined />}
            onClick={() => handleShareAll(sectionKey, shareTargets)}
            style={{ color: theme.eric.green, borderColor: theme.eric.green }}
          />
        </Tooltip>
        <Tooltip title="Don't share remaining">
          <Button
            size="small"
            data-testid="qc-dont-share-all"
            data-state="open"
            aria-label="Don't share remaining"
            disabled={busy || dontShareTargets.length === 0}
            loading={pending && dontShareTarget !== null}
            icon={<StopOutlined />}
            onClick={() =>
              setDontShareTarget({ sectionKey, items: dontShareTargets })
            }
            style={{ color: theme.eric.red, borderColor: theme.eric.red }}
          />
        </Tooltip>
      </Space>
    )
  }

  const sectionSurface = (
    choice: ReturnType<typeof getQualityCheckSectionChoice>,
    dark: boolean
  ): { border: string; background: string } => {
    if (choice === "blocked") {
      return {
        border: `1px solid ${theme.eric.red}`,
        background: `${theme.eric.red}18`,
      }
    }
    if (choice === "shared" || choice === "mixed") {
      return {
        border: `1px solid ${theme.eric.green}`,
        background: `${theme.eric.green}18`,
      }
    }
    return {
      border: "1px solid transparent",
      background: dark
        ? "radial-gradient(farthest-corner at 10px 10px, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0) 65%)"
        : "radial-gradient(farthest-corner at 10px 10px, #E6CDB8 0%, #E6CDB800 65%)",
    }
  }

  const renderHiddenToggle = (sectionKey: string, hidden: number) => {
    if (hidden <= 0) return null
    return (
      <span onClick={(event) => event.stopPropagation()}>
        <Checkbox
          data-testid="qc-show-hidden"
          checked={shownHiddenKeys.has(sectionKey)}
          onChange={(event) =>
            toggleShownHidden(sectionKey, event.target.checked)
          }
        >
          Show hidden ({hidden})
        </Checkbox>
      </span>
    )
  }

  const titleStyle = (dark: boolean): CSSProperties => ({
    fontWeight: 600,
    ...(dark ? { color: "#fff" } : {}),
  })

  const renderClassSection = (
    section: QualityCheckClassSection,
    dark: boolean,
    width: number
  ) => {
    const collapsed = section.decided && !expandedKeys.has(section.key)
    const hidden = section.allItems.length - section.items.length
    const gridItems = shownHiddenKeys.has(section.key)
      ? section.allItems
      : section.items
    const choice = getQualityCheckSectionChoice(section.allItems)
    const surface = sectionSurface(choice, dark)
    return (
      <div
        key={section.key}
        data-testid="qc-class-section"
        data-section-key={section.key}
        data-decided={section.decided ? "true" : "false"}
        data-choice={choice}
        data-collapsed={collapsed ? "true" : "false"}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          padding: 12,
          borderRadius: 8,
          border: surface.border,
          background: surface.background,
        }}
      >
        <div
          data-testid="qc-class-header"
          role={section.decided ? "button" : undefined}
          tabIndex={section.decided ? 0 : undefined}
          aria-expanded={section.decided ? !collapsed : undefined}
          onClick={
            section.decided ? () => toggleExpanded(section.key) : undefined
          }
          onKeyDown={
            section.decided
              ? (event) => {
                  if (event.key !== "Enter" && event.key !== " ") return
                  event.preventDefault()
                  toggleExpanded(section.key)
                }
              : undefined
          }
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 12,
            cursor: section.decided ? "pointer" : "default",
          }}
        >
          <Text style={titleStyle(dark)} data-testid="qc-class-title">
            {section.label}
          </Text>
          <Text type="secondary" style={secondaryTextStyle(dark)}>
            {section.allItems.length}{" "}
            {section.allItems.length === 1 ? "piece" : "pieces"}
            {hidden > 0 ? ` · ${hidden} hidden` : ""}
            {section.decided ? " · all decided" : ""}
          </Text>
          {renderHiddenToggle(section.key, hidden)}
          {renderBulkActions(section.key, section.allItems)}
        </div>
        {collapsed ? null : renderGrid(section.key, gridItems, dark, width)}
      </div>
    )
  }

  const renderLayout = (dark: boolean, width: number) => {
    if (isEmpty) {
      return (
        <Empty
          description={emptyText}
          style={{ padding: 32 }}
          data-testid="qc-tiles-empty"
        />
      )
    }
    const { competition } = layout
    const competitionHidden = competition
      ? competition.allItems.length - competition.items.length
      : 0
    const competitionItems =
      competition && shownHiddenKeys.has(competition.key)
        ? competition.allItems
        : competition?.items
    return (
      <Flex col gap={16}>
        {competition ? (
          <div
            data-testid="qc-competition"
            data-section-key={competition.key}
            data-decided={competition.decided ? "true" : "false"}
            data-choice={getQualityCheckSectionChoice(competition.allItems)}
            data-collapsed={
              competition.decided && !expandedKeys.has(competition.key)
                ? "true"
                : "false"
            }
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              padding: 12,
              borderRadius: 8,
              ...sectionSurface(
                getQualityCheckSectionChoice(competition.allItems),
                dark
              ),
            }}
          >
            <div
              role={competition.decided ? "button" : undefined}
              tabIndex={competition.decided ? 0 : undefined}
              aria-expanded={
                competition.decided
                  ? !expandedKeys.has(competition.key)
                  : undefined
              }
              onClick={
                competition.decided
                  ? () => toggleExpanded(competition.key)
                  : undefined
              }
              onKeyDown={
                competition.decided
                  ? (event) => {
                      if (event.key !== "Enter" && event.key !== " ") return
                      event.preventDefault()
                      toggleExpanded(competition.key)
                    }
                  : undefined
              }
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 12,
                cursor: competition.decided ? "pointer" : "default",
              }}
            >
              <Title level={5} style={{ margin: 0, ...titleStyle(dark) }}>
                Competition
              </Title>
              <Text type="secondary" style={secondaryTextStyle(dark)}>
                {competition.allItems.length}{" "}
                {competition.allItems.length === 1 ? "entry" : "entries"}
                {competitionHidden > 0 ? ` · ${competitionHidden} hidden` : ""}
                {competition.decided ? " · all decided" : ""}
              </Text>
              {renderHiddenToggle(competition.key, competitionHidden)}
              {renderBulkActions(competition.key, competition.allItems)}
            </div>
            {competition.decided && !expandedKeys.has(competition.key)
              ? null
              : renderGrid(
                  competition.key,
                  competitionItems ?? [],
                  dark,
                  width
                )}
          </div>
        ) : null}
        {layout.days.map((day, dayIndex) => (
          <div
            key={day.key || "undated"}
            data-testid="qc-day"
            data-day={day.key}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              ...(dayIndex > 0
                ? {
                    borderTop: `1px solid ${dark ? "rgba(255, 255, 255, 0.2)" : theme.gray[3]}`,
                    paddingTop: 16,
                  }
                : {}),
            }}
          >
            <Title
              level={5}
              style={{ margin: 0, ...titleStyle(dark) }}
              data-testid="qc-day-title"
            >
              {day.label}
            </Title>
            {day.sections.map((section) =>
              renderClassSection(section, dark, width)
            )}
          </div>
        ))}
      </Flex>
    )
  }

  return (
    // A plain div, not Flex: the Flex building block does not forward data-*
    // attributes, and the e2e spec anchors on this testid.
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
      }}
      data-testid="qc-tiles-root"
    >
      <Flex
        gap={12}
        style={{
          alignItems: "center",
          padding: "0 20px 8px",
          flex: "0 0 auto",
        }}
      >
        <Button
          icon={<FullscreenOutlined />}
          onClick={openFullscreen}
          disabled={tileCount === 0}
          data-testid="qc-fullscreen"
        >
          Full screen
        </Button>
        <TileWidthSlider
          tileWidth={tileWidth}
          dark={false}
          onChange={setTileWidth}
          testId="qc-size-slider"
        />
        {layout.queuedSectionCount > 0 ? (
          <Text type="secondary" data-testid="qc-queued">
            {layout.queuedSectionCount} more{" "}
            {layout.queuedSectionCount === 1 ? "class" : "classes"} after these
          </Text>
        ) : null}
      </Flex>

      <SScrollArea
        style={{ padding: "0 20px 20px" }}
        data-testid="qc-tiles-scroll"
      >
        {renderLayout(false, tileWidth)}
      </SScrollArea>

      <Modal
        open={isFullscreen}
        onCancel={closeFullscreen}
        footer={null}
        destroyOnClose
        width="96vw"
        style={{ top: 16, maxWidth: "none", paddingBottom: 0 }}
        styles={{
          body: {
            height: "calc(100vh - 130px)",
            overflowY: "auto",
            background: FULLSCREEN_BG,
            padding: "0 12px 12px",
          },
        }}
        title="Quality Check"
      >
        {/* Same sections as the page, so opening fullscreen never changes
            what is on screen — only how big it is. The image URLs are
            identical too, so the browser serves them from cache. */}
        <div data-testid="qc-fullscreen-body">
          <SStickyControls $dark>
            <Flex gap={12} style={{ alignItems: "center" }}>
              <Text type="secondary" style={secondaryTextStyle(true)}>
                {tileCount} {tileCount === 1 ? "tile" : "tiles"}
              </Text>
              <TileWidthSlider
                tileWidth={fullscreenTileWidth}
                dark
                onChange={setFullscreenTileWidth}
                testId="qc-size-slider"
              />
            </Flex>
          </SStickyControls>
          {renderLayout(true, fullscreenTileWidth)}
        </div>
      </Modal>

      <DontShareReasonModal
        open={dontShareTarget !== null}
        subject="photo"
        pending={bulkPendingKey !== null}
        onConfirm={handleDontShareConfirm}
        onCancel={() => {
          if (bulkPendingKey === null) setDontShareTarget(null)
        }}
      />
    </div>
  )
}

export default QualityCheckTiles
